import { useEffect, useMemo, useState } from 'react'
import { MRT_PaginationState, type MRT_ColumnDef, MRT_SortingState } from 'mantine-react-table'
import { formatDateWithoutTime, getCurrentTranslation } from '@/shared/lib/utils'
import { useTranslation } from 'react-i18next'
import { useDebounce } from '@/shared/lib/useDebounce'
import { useLanguageStore } from '@/widgets/LanguageSelect/model/store'
import { usePromotionsByPage } from '@/entities/Promotions'
import { Badge, Checkbox } from '@mantine/core'
import { Promotion } from '@/shared/api/topli/TopliApi'

export const usePromotionsTable = () => {
  const [search, setSearch] = useState('')
  const [sorting, setSorting] = useState<MRT_SortingState>([])
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  })
  const { t } = useTranslation(['promotion', 'common'])
  const { language } = useLanguageStore()
  const memoizedSorting = useMemo(() => {
    let orderBy = ''
    if (!sorting.length) return null
    sorting.forEach(item => (item.desc ? (orderBy = `-${item.id}`) : (orderBy = `${item.id}`)))
    return orderBy
  }, [sorting])
  const debouncedSearch = useDebounce(search, 500)

  const {
    data: promotions,
    isLoading: isLoadingPromotions,
    isError: isErrorPromotions,
    isFetching: isFetchingPromotions,
  } = usePromotionsByPage(
    memoizedSorting,
    pagination.pageSize,
    pagination.pageIndex * pagination.pageSize,
    debouncedSearch,
  )

  const fetchedPromotions = promotions?.data ?? []
  const totalRowCount = promotions?.total ?? 0

  useEffect(() => {
    setPagination(prev => ({ ...prev, pageIndex: 0 }))
  }, [debouncedSearch])

  const handleSearchChange = (value: string) => {
    setSearch(value)
  }

  const columns = useMemo<MRT_ColumnDef<Promotion>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 90,
      },
      {
        accessorKey: 'name',
        header: t('promotion:name'),
        Cell: ({ row }) => <div className='text-sm font-semibold'>{row.original.name}</div>,
      },
      {
        accessorKey: 'residential_projects',
        header: t('address:residentials'),
        size: 500,
        enableSorting: false,
        Cell: ({ row }) => {
          const residentials = row.original.residential_projects

          return residentials?.length
            ? residentials.map(residential => (
                <Badge
                  key={residential.id}
                  size='sm'
                  color={residential.activity ? 'green' : 'orange'}
                >
                  {getCurrentTranslation(residential, language, 'name')}
                </Badge>
              ))
            : '–'
        },
      },
      {
        accessorKey: 'start_date',
        header: t('promotion:start_date'),
        Cell: ({ row }) => (
          <div>{formatDateWithoutTime(row.original.start_date || null, language)}</div>
        ),
      },
      {
        accessorKey: 'end_date',
        header: t('promotion:end_date'),
        Cell: ({ row }) => (
          <div>
            {row.original.end_date ? formatDateWithoutTime(row.original.end_date, language) : '-'}
          </div>
        ),
      },
      {
        accessorKey: 'is_permanent',
        header: t('promotion:is_permanent'),
        Cell: ({ row }) => <Checkbox checked={!!row.original.is_permanent} readOnly />,
      },
    ],
    [language],
  )
  return {
    pagination,
    fetchedPromotions,
    totalRowCount,
    columns,
    isErrorPromotions,
    isFetchingPromotions,
    isLoadingPromotions,
    sorting,
    setSorting,
    setPagination,
    handleSearchChange,
  }
}
